import {
    inject,
    Injectable,
    Injector
} from '@angular/core';

import {
    DigitalPaymentHandler,
    DigitalPaymentHandlerOptions,
    DigitalPaymentHandlerResult
} from 'src/app/shared/model/digital-payments.model';

import {
    DocumentPrintHook,
    DocumentPrinterOptions
} from 'src/app/shared/model/document-printer.model';

import {
    Sales,
} from 'tilby-models';

import { validate as validateUuid } from 'uuid';

import {
    ConfigurationManagerService,
    EntityManagerService,
} from 'src/app/core';

import {
    ZPMSConnectorUtilsService
} from './z-pms-connector-utils.service';

import {
    ZPMSConnectorApiService
} from './z-pms-connector-api.service';

import {
    ZPMSConnector
} from './z-pms-connector.model';

import {
    ActiveSaleService
} from 'src/app/features/cashregister';
import { ZPMSConnectorDialogService } from './dialog/z-pms-connector-dialog.component';

@Injectable({
    providedIn: 'root'
})
export class ZPMSConnectorService implements DigitalPaymentHandler, DocumentPrintHook {
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly injector = inject(Injector);
    private readonly zPMSConnectorUtilsService = inject(ZPMSConnectorUtilsService);
    private readonly zPMSConnectorApi = inject(ZPMSConnectorApiService);
    private readonly zPMSConnectorDialogService = inject(ZPMSConnectorDialogService);

    async payment(amount: number, options: DigitalPaymentHandlerOptions): Promise<DigitalPaymentHandlerResult> {
        const currentSale = options.sale;
        const zPMSPaymentMethod = options.paymentMethod;
        let folio: ZPMSConnector.FolioDto | undefined;

        try {
            folio = await this.zPMSConnectorDialogService.openDialog({ data: { amount: amount, paymentMethod: zPMSPaymentMethod }});
        } catch (error) {
            throw (typeof error === 'object') ? 'CANCELED' : error;
        }

        if (!folio) {
            throw 'CANCELED';
        }

        if (options.paymentMethod.require_signature) {
            const activeSaleService = this.injector.get(ActiveSaleService);

            try {
                await activeSaleService.addSignatureToSale();
            } catch (err) {
                throw 'CANCELED';
            }
        }

        const zPMSAPI = await this.zPMSConnectorApi.getApiInstance(zPMSPaymentMethod);

        const transaction = await this.zPMSConnectorUtilsService.buildTransaction(folio, currentSale);

        try {
            const response = await zPMSAPI.createCharge(transaction);

            const folioId = response.data?.[0]?.folioId;

            if (folioId) {
                folio.folioId = folioId;
                transaction.folioId = folioId;
            }

            let responseString = '';

            try {
                responseString = JSON.stringify(transaction);
            } catch (err) {
                //Nothing to do
            }

            return {
                acquirer_name: 'PMS Zucchetti',
                payment_data: responseString,
                tail: this.zPMSConnectorUtilsService.buildTail(folio, currentSale, transaction.connectedProduct!),
                unclaimed: true
            };
        } catch (error: any) {
            if (!error) {
                throw "ERICSOFT_PMS_OFFLINE";
            }

            if (error instanceof Response) {
                switch (error.status) {
                    case 400:
                    case 401:
                        throw "ERICSOFT_PMS_UNABLE_TO_COMPLETE_TRANSACTION";
                }
            }

            throw "UNKNOWN_ERROR";
        }
    }

    async refund(amount: number, options: DigitalPaymentHandlerOptions): Promise<any> {
        const resultTemplate = {
            acquirer_name: 'PMS Zucchetti',
            payment_data: '{}',
            unclaimed: true,
        };

        const currentSale = options.sale;

        if (!currentSale) {
            return resultTemplate;
        }

        //Make sure that the referenced sale had a ZPMS payment and in that case perform the rollback
        const parentSaleId = currentSale.sale_parent_id || currentSale.sale_parent_uuid;

        if (!parentSaleId) {
            return resultTemplate;
        }

        const parentSale = validateUuid(parentSaleId.toString())
            ? await this.entityManagerService.sales.fetchCollectionOnline({ uuid: parentSaleId, pagination: false }).then((sales) => (sales as Sales[])[0])
            : await this.entityManagerService.sales.fetchOneOfflineFirst(parentSaleId);

        if (!parentSale) {
            return resultTemplate;
        }

        const zPMSPayment = parentSale.payments?.find((payment) => payment.payment_method_type_id === 43);

        if (!zPMSPayment?.payment_data) {
            return resultTemplate;
        }

        const paymentMethod = await this.entityManagerService.paymentMethods.fetchOneOffline(zPMSPayment.payment_method_id);

        if (paymentMethod?.payment_method_type_id !== 43) {
            return resultTemplate;
        }

        let zPMSApi;

        try {
            zPMSApi = await this.zPMSConnectorApi.getApiInstance(paymentMethod);
        } catch (err) {
            return resultTemplate;
        }

        let refundResult = {};

        try {
            const originalCharge = <ZPMSConnector.SalesResourceDto>JSON.parse(zPMSPayment.payment_data);
            refundResult = await zPMSApi.deleteCharge(originalCharge);
        } catch (err: any) {
            if (err instanceof Response) {
                refundResult = await err.json().catch(() => undefined);
            }
        }

        return {
            ...resultTemplate,
            payment_data: JSON.stringify(refundResult)
        };
    }

    isEnabled(): boolean {
        return true;
    }

    async postPrintHook(sale: Sales, printerDocumentData: DocumentPrinterOptions): Promise<void> {
        /*
            1. Sending other sales to Zucchetti PMS must be enabled
            2. The sale must not have a Zucchetti PMS payment
            3. zucchetti_pms.send_sale_from_be must be a false. In that case the sale will be sent to PMS from BE after sale is closed and not from FE, so the hook will not be executed
        */
        if (
            !this.configurationManagerService.getPreference('zucchetti_pms.send_other_sales') ||
            sale.payments?.some((payment) => payment.payment_method_type_id == 43) ||
            this.configurationManagerService.getPreference('zucchetti_pms.send_sale_from_be')
        ) {
            return;
        }

        const zPMSPaymentMethod = await this.entityManagerService.paymentMethods.fetchCollectionOffline({ payment_method_type_id: 43 }).then((methods) => methods[0]);

        if (!zPMSPaymentMethod) {
            return;
        }

        this.zPMSConnectorUtilsService.buildExtTransaction({ folioId: undefined }, sale).then((transaction) => {
            this.zPMSConnectorApi.getApiInstance(zPMSPaymentMethod)
                .then((zPMSApi) => zPMSApi.createDocument(transaction));
        });
    }

    async printFailHook(sale: Sales, printerDocumentData: DocumentPrinterOptions): Promise<string | undefined> {
        const zPMSPayment = sale.payments?.find((payment) => payment.payment_method_type_id === 43);

        if (!zPMSPayment?.payment_data) {
            return;
        }

        const zPMSPaymentMethod = await this.entityManagerService.paymentMethods.fetchOneOffline(zPMSPayment.payment_method_id);

        if (!zPMSPaymentMethod) {
            return;
        }

        let result = 'KO';

        try {
            const originalCharge = <ZPMSConnector.SalesResourceDto>JSON.parse(zPMSPayment.payment_data);

            const zPMSApi = await this.zPMSConnectorApi.getApiInstance(zPMSPaymentMethod);
            await zPMSApi.deleteCharge(originalCharge);

            result = 'OK';
        } catch (err) {
            //Nothing to do
        } finally {
            throw `ERICSOFT_PMS_ROLLBACK_${result}`;
        }
    }
}